import React from 'react';
import { ToolContainer } from './ToolContainer';
import type { ToolId } from '../utils/slugs';
import { InfoIcon } from '../Icons';
import { BakersPercentageClient } from './ui/BakersPercentageClient';

interface BakersPercentageProps {
    setActiveTool: (toolId: ToolId) => void;
    breadcrumbs: React.ReactNode;
}

export const BakersPercentage: React.FC<BakersPercentageProps> = ({ setActiveTool, breadcrumbs }) => {
    return (
        <ToolContainer
            title="Free Baker's Percentage Calculator"
            description="Master bread and dough formulas with our free baker's math calculator. Instantly calculate ingredient weights, percentages, and total dough hydration for perfect results."
            setActiveTool={setActiveTool}
            breadcrumbs={breadcrumbs}
            toolId="bakers-percentage"
        >
            <BakersPercentageClient />
            <BakersPercentageContent />
        </ToolContainer>
    );
};

const BakersPercentageContent: React.FC = () => (
    <div className="mt-12 space-y-12 pt-8 border-t border-brand-border">
        <div className="prose prose-pink max-w-none">
            <h2 className="font-serif text-3xl font-bold text-brand-text-title mb-4">Master Baker's Math & Hydration</h2>
            <p className="text-lg text-brand-text-body/80 leading-relaxed">
                Professional bakers don't use cups; they use <strong>Baker's Percentages</strong>. This distinctive formula sets the total weight of flour as 100%, and all other ingredients as a percentage of that flour. This makes scaling recipes up (for a bakery) or down (for a single loaf) incredibly easy. Our <strong>Bread Hydration Calculator</strong> helps you find the perfect water-to-flour ratio for everything from stiff <strong>Bagel dough (55%)</strong> to airy <strong>Ciabatta (80%)</strong>.
            </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white p-6 rounded-2xl border border-brand-border shadow-sm">
                <h3 className="font-serif font-bold text-xl mb-4 text-brand-text-title flex items-center gap-2">
                    <InfoIcon className="w-6 h-6 text-brand-pink" />
                    How to Use Baker's Math
                </h3>
                <ol className="space-y-3 text-sm text-brand-text-body/80 list-decimal pl-4 marker:text-brand-pink marker:font-bold">
                    <li><strong>Set Base Flour:</strong> Enter the total amount of flour in grams. This is always your <strong>100%</strong>.</li>
                    <li><strong>Add Ingredients:</strong> Input weights for water, salt, yeast, and starters.</li>
                    <li><strong>Analyze Hydration:</strong>
                        <ul className="list-disc pl-4 mt-1 space-y-1">
                            <li><strong>50-60%:</strong> Stiff dough (Bagels, Pretzels).</li>
                            <li><strong>60-70%:</strong> Standard dough (Sandwich bread, Pizza).</li>
                            <li><strong>70-80%+:</strong> Wet/High hydration dough (Sourdough, Ciabatta).</li>
                        </ul>
                    </li>
                </ol>
            </div>

            <div className="bg-white p-6 rounded-2xl border border-brand-border shadow-sm">
                <h3 className="font-serif font-bold text-xl mb-4 text-brand-text-title">Frequently Asked Questions</h3>
                <div className="space-y-4">
                    <details className="group">
                        <summary className="flex justify-between items-center font-bold text-brand-text-title cursor-pointer hover:text-brand-pink transition-colors">
                            Why uses grams instead of volume?
                            <span className="text-brand-pink group-open:rotate-180 transition-transform">▼</span>
                        </summary>
                        <p className="text-sm text-brand-text-body/80 mt-2 leading-relaxed">
                            Volume is inaccurate. One cup of flour can weigh anywhere from 120g to 150g depending on how it's packed. For consistent <strong>sourdough starter</strong> and bread results, weighing ingredients is non-negotiable.
                        </p>
                    </details>
                    <details className="group">
                        <summary className="flex justify-between items-center font-bold text-brand-text-title cursor-pointer hover:text-brand-pink transition-colors">
                            Does sourdough starter count as hydration?
                            <span className="text-brand-pink group-open:rotate-180 transition-transform">▼</span>
                        </summary>
                        <p className="text-sm text-brand-text-body/80 mt-2 leading-relaxed">
                            Yes! A standard 100% hydration starter is composed of equal parts flour and water.
                        </p>
                    </details>
                </div>
            </div>
        </div>
    </div>
);
